import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AppropriatenessTestMarginRequirementAtLeverage } from '@/features/futures/schemas/appropriateness-test';
import { usePromise } from '@/hooks/use-promise';
import { FUTURES_APPROPRIATENESS_MARGIN_REQUIREMENT_AT_LEVERAGE_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.marginRequirementAtLeverage.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.marginRequirementAtLeverage.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend.displayName = 'FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend';

const FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage = () => {
  const form = useFormContext<Partial<AppropriatenessTestMarginRequirementAtLeverage>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="marginRequirementAtLeverage"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.marginRequirementAtLeverage.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.marginRequirementAtLeverage.title')}
              onValueChange={(value) => {
                field.onChange(value);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_MARGIN_REQUIREMENT_AT_LEVERAGE_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.marginRequirementAtLeverage.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage.displayName = 'FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage';
FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage.isAutoNextStep = true;

export { FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage, FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend };
